import {useAuth} from '../auth/use-auth';
import {useSettings} from '@ui/settings/use-settings';
import dot from 'dot-object';
import {useMemo} from 'react';
import {User} from '@ui/types/user';
import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';

const contactInboxIcon: MenuItemConfig['icon'] = [
  {
    tag: 'path',
    attr: {
      d: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5v-3h3.56c.69 1.19 1.97 2 3.45 2s2.75-.81 3.45-2H19v3zm0-5h-4.99c0 1.1-.9 2-2 2s-2-.9-2-2H5V5h14v9z',
    },
  },
];

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    const menuItems: MenuItemConfig[] = [];

    if (menu) {
      for (const item of menu.items) {
        const hasRoles = (item.roles || []).every(
          a => user?.roles.find(b => b.id === a),
        );
        const hasPermissions = (item.permissions || []).every(a =>
          hasPermission(a),
        );
        const hasSettings =
          !item.settings ||
          Object.entries(item.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        if (
          !item.action ||
          !hasRoles ||
          !hasPermissions ||
          !hasSettings ||
          !subscriptionStatusMatches(item, user)
        ) {
          continue;
        }

        if (user?.id) {
          item.action = item.action.replace(/{currentUser}/g, `${user.id}`);
        }

        if (
          menuOrPosition === 'admin-sidebar' &&
          item.action === '/admin/contact-messages' &&
          !item.icon
        ) {
          item.icon = contactInboxIcon;
        }

        menuItems.push(item);
      }
      menu.items = menuItems;
    }

    return menu;
  }, [hasPermission, settings, menuOrPosition, user]);
}

function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }
  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);
  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }
  return !hasActiveSubscription;
}
